import React, { useState, useEffect, useRef } from 'react';
import ApexCharts from 'apexcharts';
import ItemService from '../../../services/supplier_appCommonService';
import LoaderSmall from '../../../Components/LoaderSmall';

const PieChartCard = ({
  title,
  chartConfigs = [],
  height = 350,
  chartType = 'pie',
  showToolbar = false,
  legendPosition = 'right',
}) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const isDestroyingRef = useRef(false);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchChartData = async () => {
      try {
        setLoading(true);

        const promises = chartConfigs.map(async (config) => {
          try {
            const res = config.fetchFunction
              ? await config.fetchFunction()
              : await ItemService.generateQuery({ queryName: config.queryName });

            let value = 0;

            if (
              typeof res?.data === 'object' &&
              res?.data !== null &&
              !Array.isArray(res.data)
            ) {
              value = res.data.count ?? 0;
            } else if (Array.isArray(res?.data) && res.data[0]?.count !== undefined) {
              value = res.data[0]?.count ?? 0;
            } else if (typeof res?.data === 'number') {
              value = res.data;
            } else if (typeof res?.total === 'number') {
              value = res.total;
            }

            if (
              config.valueKey &&
              Array.isArray(res?.data) &&
              typeof res?.total === 'number'
            ) {
              if (value === 0) {
                value = res.data.reduce((sum, row) => {
                  const v = Number(row?.[config.valueKey]);
                  return sum + (isNaN(v) ? 0 : v);
                }, 0);
              }
            }

            return {
              label: config.label,
              value: value,
              color: config.color,
            };
          } catch (err) {
            console.error(`Error fetching ${config.label}:`, err);
            return { label: config.label, value: 0, color: config.color };
          }
        });

        const results = await Promise.all(promises);
        if (mounted) setChartData(results);
      } catch (error) {
        console.error('Chart data error:', error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchChartData();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line 
  }, []);

  useEffect(() => {
    if (!chartRef.current || chartData.length === 0 || loading) return;

    const series = chartData.map((item) => item.value);
    const labels = chartData.map((item) => item.label);
    const colors = chartData.map((item) => item.color).filter(Boolean);

    const options = {
      series: series,
      chart: {
        height: height,
        type: chartType,
        toolbar: { show: showToolbar },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
        },
      },
      labels: labels,
      colors: colors.length === labels.length ? colors : undefined,
      dataLabels: {
        enabled: true,
        formatter: (val) => val.toFixed(1) + '%',
        style: {
          fontSize: '14px',
          fontWeight: 'bold',
          colors: ['#fff'],
        },
      },
      legend: {
        show: true,
        position: legendPosition,
        fontSize: '14px',
        markers: {
          width: 12,
          height: 12,
          radius: 12,
        },
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            legend: { position: 'bottom' },
          },
        },
      ],
    };

    const destroyChart = () => {
      if (chartInstanceRef.current && !isDestroyingRef.current) {
        isDestroyingRef.current = true;
        try {
          chartInstanceRef.current.destroy();
        } catch (e) {
          // Ignore
        } finally {
          chartInstanceRef.current = null;
          isDestroyingRef.current = false;
        }
      }
    };

    destroyChart();

    const timer = setTimeout(() => {
      if (chartRef.current && !isDestroyingRef.current) {
        chartInstanceRef.current = new ApexCharts(chartRef.current, options);
        chartInstanceRef.current.render();
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      destroyChart();
    };
  }, [chartData, loading, height, chartType, showToolbar, legendPosition]);

  return (
    <>
      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
          <LoaderSmall isLoading />
        </div>
      ) : (
        <div ref={chartRef}></div>
      )}
    </>
  );
};

export default PieChartCard;
